import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { getQuerystringValue, notifyError } from "utils";
import { isNonEmptyArray, isNonEmptyString, isNonEmptyObject } from "@cayuse/shared-react.utils.type-checks";
import { completeReviewTask, completeMultipleReviewTasks } from "store/actions/userTasks";
import Button from "@cayuse/shared-react.components.button";
import ButtonGroup from "@cayuse/shared-react.components.button-group";
import Dropdown from "components/Dropdown";
import Modal from "@cayuse/shared-react.components.modal";
import RequiredIndicator from "components/RequiredIndicator";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import TextArea from "@cayuse/shared-react.components.form.inputs.text-area";


import styles from "./styles.css";


const SINGLE_MODE = "SINGLE_MODE";
const MULTI_MODE = "MULTI_MODE";
const DEFAULT_DECISION = "approved";
const COMPLETE_ALL_ID = "COMPLETE_ALL_REVIEWS";

const CompleteReview = ({
	formInstance,
	instanceId,
	instanceTasks,
	instanceFeedEndpoint,
	dictionary,
	getInstance,
	filterInstanceReviewTasks,
	isCompleteReviewActive = true,
	decisionOptions

}) => {
	const [ isLoading, setIsLoading ] = useState(false);
	const { search } = useLocation();
	const [ preselectedTaskId, setPreselectedTaskId ] = useState(null);
	const [ isModalOpen, setIsModalOpen ] = useState(false);
	const [ selectedReviews, setSelectedReviews ] = useState([]);
	const [ decision, setDecision ] = useState(DEFAULT_DECISION);
	const [ comment, setComment ] = useState("");
	const [ disabledReason, setDisabledReason ] = useState(null);
	const [ reviews, setReviews ] = useState([]);
	const isCommentRequired = decision === "declined" || decision === "deferred";
	const numReviews = isNonEmptyArray(reviews) ? reviews.length : 0;
	const hasActiveReviews = numReviews > 0;

	const reviewMode =  useMemo(() => {
		if (selectedReviews.length > 1) {
			return MULTI_MODE;
		}
		if (selectedReviews.length > 0) {
			return SINGLE_MODE;
		}

		return null;
	}, [ selectedReviews ]);

	const reviewingFor = useMemo(() => {
		if (reviewMode === MULTI_MODE) {
			return dictionary.modalTitleMultipleRules;
		}
		if (reviewMode === SINGLE_MODE) {
			const typeSuffix = dictionary.taskTypeHeaderDisplays[selectedReviews[0].taskType] || "";
			return `${selectedReviews[0].targetName} ${typeSuffix ? typeSuffix : ""}` || "";
		}
	}, [ reviewMode, selectedReviews, dictionary.taskTypeHeaderDisplays ]);

	const nextTargetsNames = useMemo(() => {
		if (selectedReviews.length > 0) {
			return selectedReviews.map(selectedReview => selectedReview.nextTargetName).filter(Boolean);
		}

		return [];
	}, [ selectedReviews ]);

	const resetData = () => {
		setComment("");
		setDecision(DEFAULT_DECISION);
		setSelectedReviews([]);
	};

	const onModalClose = () => {
		setIsModalOpen(false);
		resetData();
	};

	const handleSave = () => {
		setIsLoading(true);
		let reviewPromise;
		if (reviewMode === MULTI_MODE) {
			const tasks = selectedReviews.map(review => ({ taskId: review.id, targetName: review.targetName }));
			reviewPromise = completeMultipleReviewTasks(instanceId, instanceFeedEndpoint, {
				tasks,
				decision,
				comment
			});
		} else if (reviewMode === SINGLE_MODE) {
			reviewPromise = completeReviewTask(instanceId, instanceFeedEndpoint, {
				taskId: selectedReviews[0].id,
				decision,
				comment,
				taskType: selectedReviews[0].taskType,
				targetName: selectedReviews[0].targetName,
			});
		}

		if (!!reviewPromise) {
			reviewPromise.then(() => {
				onModalClose();
				getInstance(instanceId);
			})
				.catch((e) => {
					const message = dictionary.errors.unableToSave || "Unable to complete the review";
					notifyError(message, e);
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	};

	const handleCancel = () => {
		onModalClose();
	};

	const onCommentChange = value => {
		setComment(value);
	};

	const onDecisionChange = value => {
		setDecision(value);
	};

	useEffect(() => {
		if (isNonEmptyObject(formInstance) && isNonEmptyArray(instanceTasks)) {
			const reviewTasks = filterInstanceReviewTasks(formInstance, instanceTasks);

			if (isNonEmptyArray(reviewTasks) && reviewTasks.length === 1) {
				setSelectedReviews([ reviewTasks[0] ]);
			}

			setReviews(reviewTasks);
		} else {
			setReviews([]);
			setSelectedReviews([]);
		}
	}, [ formInstance, instanceTasks ]);

	useEffect(() => {
		const queryStringTaskId = getQuerystringValue("task");

		if (!preselectedTaskId || preselectedTaskId !== queryStringTaskId) {
			const preselectedTask = isNonEmptyString(queryStringTaskId)
				? reviews.find(({ id }) => id === queryStringTaskId)
				: undefined;

			if (preselectedTask) {
				setPreselectedTaskId(queryStringTaskId);
				setSelectedReviews([ preselectedTask ]);
				setIsModalOpen(true);
			}
		}
	}, [ reviews, preselectedTaskId, instanceId, search ]);

	const renderSingleRoleButton = () => {
		return (
			<Button primary onClick={() => setIsModalOpen(true)} disabled={!hasActiveReviews || !isCompleteReviewActive}>
				{dictionary.buttonLabel || "Complete Review"}
			</Button>
		);
	};

	const handleRoleSelection = chosenReview => {
		setIsModalOpen(true);
		if (chosenReview.value === COMPLETE_ALL_ID) {
			setSelectedReviews(reviews);
		} else {
			const taskId = chosenReview.value;
			const task = reviews.find(review => review.id === taskId);
			setSelectedReviews([ task ]);
		}
	};

	const renderMultiRoleButton = () => {
		const buttonText = `${(dictionary.buttonLabel || "Complete Review")} (${numReviews})`;
		const onChange = (e, selection) => {
			const { id, text, value } = selection;
			handleRoleSelection({ id, text, value });
		};
		let options = [];
		if (hasActiveReviews) {
			options = reviews.map(review => ({ value: review.id, text: review.name, onClick: onChange }));
			options.push({ value: COMPLETE_ALL_ID, text: dictionary.completeAll || "Complete all Reviews", onClick: onChange });
		}

		return (
			<Dropdown
				className={styles.completeReviewsDropdown}
				button
				text={buttonText}
				selectOnBlur={false}
				options={options}
				defaultValue={""}
				disabled={isModalOpen}
			/>
		);
	};


	useEffect(() => {
		if (!decision) {
			setDisabledReason(dictionary.errors?.noDecision || "A review decision is required");
		} else if (isCommentRequired && !isNonEmptyString(comment)) {
			setDisabledReason(dictionary.errors?.noComment || "A comment is required");
		} else {
			setDisabledReason(null);
		}
	}, [ decision, comment, isCommentRequired, dictionary.errors ]);

	return (
		<div data-component="ProposalSummary\CompleteReview">
			{numReviews > 1
				? renderMultiRoleButton()
				: renderSingleRoleButton()
			}

			<Modal closeIcon open={isModalOpen} onClose={onModalClose} ariaLabel={`${dictionary.modalTitle || "Complete Review"} ${reviewingFor && ` ${dictionary.modalTitleSeparator || "for"} ${reviewingFor}`}`}>
				<Modal.Header className="ui header whiteOnBlue">
					<h2>{dictionary.modalTitle || "Complete Review"} { reviewingFor && ` ${dictionary.modalTitleSeparator || "for"} ${reviewingFor}`}</h2>
				</Modal.Header>

				<Modal.Content>
					{reviewMode === MULTI_MODE && (
						<div className={styles.reviewNames}>
							{selectedReviews.map(review => (<span key={review.id}>{review.name}</span>))}
						</div>
					)}
					<div className={`${styles.fieldset} ${styles.decisionWrapper}`}>
						<label htmlFor={"decision"}>
							{dictionary.fields?.reviewDecision || "Review Decision"}
							<RequiredIndicator />
						</label>
						<Dropdown
							id="decision"
							className={styles.field}
							selection
							fluid
							options={decisionOptions}
							onChange={(e, { value }) => onDecisionChange(value)}
							value={decision}
						/>
					</div>
					<div className={`ui input ${styles.fieldset}`}>
						<label>
							{dictionary.fields?.comment || "Comment"}
							{isCommentRequired && <RequiredIndicator />}
						</label>
					</div>
					<TextArea
						placeholder={dictionary.commentPlaceholder || "Please add a comment."}
						value={comment}
						onChange={event => onCommentChange(event.target.value)}
					/>
					<div className={styles.additionalReviewText}>
						{nextTargetsNames.map((nextTargetName, index) => (
							<span key={`${nextTargetName}-${index}`}>
								Additional review required. Routing to {nextTargetName} next.
							</span>
						))}
					</div>
				</Modal.Content>

				<VerticalSpacing/>

				<Modal.Actions>
					<ButtonGroup>
						<Button secondary onClick={handleCancel}>
							{dictionary.cancelLabel || "Cancel"}
						</Button>

						<Button primary
								onClick={handleSave}
								disabled={isNonEmptyString(disabledReason) || isLoading}
								busy={isLoading}
								loadingMessage="Save in process"
								tooltip={disabledReason}
						>
							{dictionary.saveLabel || "Save"}
						</Button>
					</ButtonGroup>
				</Modal.Actions>
			</Modal>
		</div>
	);
};

CompleteReview.propTypes = {
	onCancel: PropTypes.func,
	onSave: PropTypes.func,
	tasks: PropTypes.arrayOf(PropTypes.shape({

	}))
};

export default CompleteReview;
